import { Language, Translation } from '../types';

export const translations: Record<Language, Translation> = {
  en: {
    // Navigation
    home: 'Home',
    conditions: 'Conditions',
    drugs: 'Drug Database',
    emergencies: 'Emergency Protocols',
    calculators: 'Calculators',
    about: 'About',
    pricing: 'Pricing',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    
    // Homepage
    heroTitle: 'Clinical Decision Support for Africa',
    heroSubtitle: 'Evidence-based guidance • Resource-appropriate protocols • Offline-capable',
    searchPlaceholder: 'Search conditions, symptoms, or specialties...',
    clinicalConditions: 'Clinical Conditions',
    evidenceBasedProtocols: 'Evidence-based protocols',
    essentialMedicines: 'Essential Medicines',
    whoEssentialList: 'WHO Essential List',
    emergencyProtocols: 'Emergency Protocols',
    urgentConditions: 'Urgent conditions',
    emergencyConditionsTitle: 'Emergency Conditions',
    viewAll: 'View all',
    
    // Features
    builtForAfrica: 'Built for African Healthcare Settings',
    resourceAppropriate: 'Resource-Appropriate',
    resourceAppropriateDesc: 'Protocols for BASIC, INTERMEDIATE, and ADVANCED care levels',
    mobileFirst: 'Mobile-First Design',
    mobileFirstDesc: 'Works seamlessly on smartphones and tablets',
    offlineCapable: 'Offline-Capable',
    offlineCapableDesc: 'Access critical information without internet',
    
    // Resource Levels
    basic: 'BASIC',
    basicDesc: 'Primary Care',
    intermediate: 'INTERMEDIATE',
    intermediateDesc: 'District Hospital',
    advanced: 'ADVANCED',
    advancedDesc: 'Referral Hospital',
    urgent: 'URGENT',
    
    // Condition Details
    overview: 'Overview',
    epidemiology: 'Epidemiology',
    clinicalPresentation: 'Clinical Presentation',
    diagnosis: 'Diagnosis',
    differentialDiagnosis: 'Differential Diagnosis',
    investigations: 'Investigations',
    management: 'Management',
    immediateActions: 'Immediate Actions',
    pharmacological: 'Pharmacological Treatment',
    nonPharmacological: 'Non-Pharmacological',
    referralCriteria: 'Referral Criteria',
    redFlags: 'Red Flags',
    complications: 'Complications',
    prognosis: 'Prognosis',
    prevention: 'Prevention',
    quickReference: 'Quick Reference',
    specialty: 'Specialty',
    bodySystem: 'Body System',
    icdCode: 'ICD Code',
    resourceLevel: 'Resource Level',
    
    // Drug Details
    drugClass: 'Drug Class',
    mechanism: 'Mechanism of Action',
    indications: 'Indications',
    contraindications: 'Contraindications',
    sideEffects: 'Side Effects',
    interactions: 'Drug Interactions',
    dosing: 'Dosing',
    monitoring: 'Monitoring',
    
    // Pricing
    freePlan: 'Free',
    proPlan: 'Professional',
    enterprisePlan: 'Enterprise',
    perMonth: '/month',
    forever: 'forever',
    getStarted: 'Get Started',
    mostPopular: 'Most Popular',
    
    // Auth
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    fullName: 'Full Name',
    organization: 'Organization',
    country: 'Country',
    role: 'Role',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    forgotPassword: 'Forgot Password?',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    
    // Footer
    footerText: 'Clinical decision support powered by evidence-based medicine',
    division: 'Division 3',
    
    // Common
    loading: 'Loading...',
    error: 'Error',
    save: 'Save',
    cancel: 'Cancel',
    submit: 'Submit',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    search: 'Search',
    filter: 'Filter',
    clear: 'Clear',
    allSpecialties: 'All Specialties',
    allLevels: 'All Levels',
    showing: 'Showing',
    of: 'of',
    results: 'results',
    noResults: 'No results found',
    tryAdjusting: 'Try adjusting your search or filters'
  },
  
  fr: {
    home: 'Accueil',
    conditions: 'Pathologies',
    drugs: 'Base de Médicaments',
    emergencies: 'Protocoles d\'Urgence',
    calculators: 'Calculateurs',
    about: 'À Propos',
    pricing: 'Tarifs',
    login: 'Connexion',
    register: 'S\'inscrire',
    logout: 'Déconnexion',
    heroTitle: 'Aide à la Décision Clinique pour l\'Afrique',
    heroSubtitle: 'Protocoles basés sur les preuves • Adaptés aux ressources • Hors ligne',
    searchPlaceholder: 'Rechercher pathologies, symptômes ou spécialités...',
    clinicalConditions: 'Pathologies Cliniques',
    evidenceBasedProtocols: 'Protocoles basés sur les preuves',
    essentialMedicines: 'Médicaments Essentiels',
    whoEssentialList: 'Liste OMS',
    emergencyProtocols: 'Protocoles d\'Urgence',
    urgentConditions: 'Conditions urgentes',
    emergencyConditionsTitle: 'Urgences',
    viewAll: 'Voir tout',
    builtForAfrica: 'Conçu pour les Systèmes de Santé Africains',
    resourceAppropriate: 'Adapté aux Ressources',
    resourceAppropriateDesc: 'Protocoles pour niveaux BASIQUE, INTERMÉDIAIRE et AVANCÉ',
    mobileFirst: 'Mobile d\'Abord',
    mobileFirstDesc: 'Fonctionne sur smartphones et tablettes',
    offlineCapable: 'Hors Ligne',
    offlineCapableDesc: 'Accès aux informations sans internet',
    basic: 'BASIQUE',
    basicDesc: 'Soins Primaires',
    intermediate: 'INTERMÉDIAIRE',
    intermediateDesc: 'Hôpital de District',
    advanced: 'AVANCÉ',
    advancedDesc: 'Hôpital de Référence',
    urgent: 'URGENT',
    overview: 'Aperçu',
    clinicalPresentation: 'Présentation Clinique',
    diagnosis: 'Diagnostic',
    management: 'Prise en Charge',
    redFlags: 'Signes d\'Alarme',
    quickReference: 'Référence Rapide',
    specialty: 'Spécialité',
    bodySystem: 'Système',
    icdCode: 'Code CIM',
    resourceLevel: 'Niveau de Ressources',
    freePlan: 'Gratuit',
    proPlan: 'Professionnel',
    enterprisePlan: 'Entreprise',
    perMonth: '/mois',
    forever: 'pour toujours',
    getStarted: 'Commencer',
    mostPopular: 'Plus Populaire',
    email: 'Email',
    password: 'Mot de passe',
    fullName: 'Nom complet',
    signIn: 'Se connecter',
    signUp: 'S\'inscrire',
    footerText: 'Aide à la décision clinique basée sur les preuves',
    division: 'Division 3',
    loading: 'Chargement...',
    search: 'Rechercher',
    filter: 'Filtrer',
    allSpecialties: 'Toutes Spécialités',
    allLevels: 'Tous Niveaux',
    showing: 'Affichage',
    of: 'sur',
    results: 'résultats',
    noResults: 'Aucun résultat',
    tryAdjusting: 'Essayez d\'ajuster votre recherche'
  },
  
  pt: {
    home: 'Início',
    conditions: 'Condições',
    drugs: 'Base de Medicamentos',
    emergencies: 'Protocolos de Emergência',
    calculators: 'Calculadoras',
    about: 'Sobre',
    pricing: 'Preços',
    login: 'Entrar',
    register: 'Registrar',
    logout: 'Sair',
    heroTitle: 'Suporte à Decisão Clínica para África',
    heroSubtitle: 'Protocolos baseados em evidências • Apropriados aos recursos • Offline',
    searchPlaceholder: 'Pesquisar condições, sintomas ou especialidades...',
    clinicalConditions: 'Condições Clínicas',
    evidenceBasedProtocols: 'Protocolos baseados em evidências',
    essentialMedicines: 'Medicamentos Essenciais',
    whoEssentialList: 'Lista OMS',
    emergencyProtocols: 'Protocolos de Emergência',
    urgentConditions: 'Condições urgentes',
    emergencyConditionsTitle: 'Emergências',
    viewAll: 'Ver todos',
    builtForAfrica: 'Construído para Sistemas de Saúde Africanos',
    resourceAppropriate: 'Apropriado aos Recursos',
    resourceAppropriateDesc: 'Protocolos para níveis BÁSICO, INTERMEDIÁRIO e AVANÇADO',
    mobileFirst: 'Mobile Primeiro',
    mobileFirstDesc: 'Funciona em smartphones e tablets',
    offlineCapable: 'Funciona Offline',
    offlineCapableDesc: 'Acesso a informações sem internet',
    basic: 'BÁSICO',
    basicDesc: 'Atenção Primária',
    intermediate: 'INTERMEDIÁRIO',
    intermediateDesc: 'Hospital Distrital',
    advanced: 'AVANÇADO',
    advancedDesc: 'Hospital de Referência',
    urgent: 'URGENTE',
    overview: 'Visão Geral',
    clinicalPresentation: 'Apresentação Clínica',
    diagnosis: 'Diagnóstico',
    management: 'Manejo',
    redFlags: 'Sinais de Alarme',
    quickReference: 'Referência Rápida',
    specialty: 'Especialidade',
    bodySystem: 'Sistema',
    icdCode: 'Código CID',
    resourceLevel: 'Nível de Recursos',
    freePlan: 'Gratuito',
    proPlan: 'Profissional',
    enterprisePlan: 'Empresarial',
    perMonth: '/mês',
    forever: 'para sempre',
    getStarted: 'Começar',
    mostPopular: 'Mais Popular',
    email: 'Email',
    password: 'Senha',
    fullName: 'Nome completo',
    signIn: 'Entrar',
    signUp: 'Registrar',
    footerText: 'Suporte à decisão clínica baseado em evidências',
    division: 'Divisão 3',
    loading: 'Carregando...',
    search: 'Pesquisar',
    filter: 'Filtrar',
    allSpecialties: 'Todas Especialidades',
    allLevels: 'Todos Níveis',
    showing: 'Mostrando',
    of: 'de',
    results: 'resultados',
    noResults: 'Nenhum resultado',
    tryAdjusting: 'Tente ajustar sua pesquisa'
  },
  
  ar: {
    home: 'الرئيسية',
    conditions: 'الحالات',
    drugs: 'قاعدة الأدوية',
    emergencies: 'بروتوكولات الطوارئ',
    calculators: 'الآلات الحاسبة',
    about: 'حول',
    pricing: 'الأسعار',
    login: 'تسجيل الدخول',
    register: 'إنشاء حساب',
    logout: 'تسجيل الخروج',
    heroTitle: 'دعم القرار السريري لأفريقيا',
    heroSubtitle: 'بروتوكولات قائمة على الأدلة • ملائمة للموارد • تعمل بدون إنترنت',
    searchPlaceholder: 'البحث عن الحالات والأعراض والتخصصات...',
    clinicalConditions: 'الحالات السريرية',
    evidenceBasedProtocols: 'بروتوكولات قائمة على الأدلة',
    essentialMedicines: 'الأدوية الأساسية',
    whoEssentialList: 'قائمة منظمة الصحة العالمية',
    emergencyProtocols: 'بروتوكولات الطوارئ',
    urgentConditions: 'حالات عاجلة',
    emergencyConditionsTitle: 'حالات الطوارئ',
    viewAll: 'عرض الكل',
    builtForAfrica: 'مصمم لأنظمة الرعاية الصحية الأفريقية',
    resourceAppropriate: 'ملائم للموارد',
    resourceAppropriateDesc: 'بروتوكولات للمستويات الأساسية والمتوسطة والمتقدمة',
    mobileFirst: 'تصميم للجوال أولاً',
    mobileFirstDesc: 'يعمل على الهواتف والأجهزة اللوحية',
    offlineCapable: 'يعمل بدون إنترنت',
    offlineCapableDesc: 'الوصول للمعلومات الحرجة بدون اتصال',
    basic: 'أساسي',
    basicDesc: 'الرعاية الأولية',
    intermediate: 'متوسط',
    intermediateDesc: 'مستشفى المنطقة',
    advanced: 'متقدم',
    advancedDesc: 'مستشفى الإحالة',
    urgent: 'عاجل',
    overview: 'نظرة عامة',
    clinicalPresentation: 'العرض السريري',
    diagnosis: 'التشخيص',
    management: 'العلاج',
    redFlags: 'علامات التحذير',
    quickReference: 'مرجع سريع',
    specialty: 'التخصص',
    bodySystem: 'الجهاز',
    icdCode: 'رمز ICD',
    resourceLevel: 'مستوى الموارد',
    freePlan: 'مجاني',
    proPlan: 'احترافي',
    enterprisePlan: 'مؤسسي',
    perMonth: '/شهر',
    forever: 'للأبد',
    getStarted: 'ابدأ الآن',
    mostPopular: 'الأكثر شعبية',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    fullName: 'الاسم الكامل',
    signIn: 'تسجيل الدخول',
    signUp: 'إنشاء حساب',
    footerText: 'دعم القرار السريري المبني على الأدلة',
    division: 'القسم 3',
    loading: 'جاري التحميل...',
    search: 'بحث',
    filter: 'تصفية',
    allSpecialties: 'جميع التخصصات',
    allLevels: 'جميع المستويات',
    showing: 'عرض',
    of: 'من',
    results: 'نتائج',
    noResults: 'لا توجد نتائج',
    tryAdjusting: 'حاول تعديل البحث'
  },
  
  sw: {
    home: 'Nyumbani',
    conditions: 'Magonjwa',
    drugs: 'Hifadhi ya Dawa',
    emergencies: 'Dharura',
    calculators: 'Vikokotoo',
    about: 'Kuhusu',
    pricing: 'Bei',
    login: 'Ingia',
    register: 'Jisajili',
    logout: 'Ondoka',
    heroTitle: 'Msaada wa Maamuzi ya Kliniki kwa Afrika',
    heroSubtitle: 'Itifaki za ushahidi • Zinazofaa kwa rasilimali • Bila mtandao',
    searchPlaceholder: 'Tafuta magonjwa, dalili, au utaalamu...',
    clinicalConditions: 'Hali za Kliniki',
    evidenceBasedProtocols: 'Itifaki za ushahidi',
    essentialMedicines: 'Dawa Muhimu',
    whoEssentialList: 'Orodha ya WHO',
    emergencyProtocols: 'Itifaki za Dharura',
    urgentConditions: 'Hali za haraka',
    emergencyConditionsTitle: 'Dharura',
    viewAll: 'Angalia zote',
    builtForAfrica: 'Imeundwa kwa Mifumo ya Afya ya Afrika',
    resourceAppropriate: 'Inafaa kwa Rasilimali',
    resourceAppropriateDesc: 'Itifaki kwa viwango vya MSINGI, WASTANI na JUU',
    mobileFirst: 'Simu Kwanza',
    mobileFirstDesc: 'Inafanya kazi kwenye simu na kompyuta kibao',
    offlineCapable: 'Bila Mtandao',
    offlineCapableDesc: 'Pata habari bila mtandao',
    basic: 'MSINGI',
    basicDesc: 'Huduma ya Msingi',
    intermediate: 'WASTANI',
    intermediateDesc: 'Hospitali ya Wilaya',
    advanced: 'JUU',
    advancedDesc: 'Hospitali ya Rufaa',
    urgent: 'HARAKA',
    overview: 'Muhtasari',
    clinicalPresentation: 'Uwasilishaji wa Kliniki',
    diagnosis: 'Uchunguzi',
    management: 'Usimamizi',
    redFlags: 'Ishara za Hatari',
    quickReference: 'Rejea Haraka',
    specialty: 'Utaalamu',
    bodySystem: 'Mfumo',
    icdCode: 'Msimbo wa ICD',
    resourceLevel: 'Kiwango cha Rasilimali',
    freePlan: 'Bure',
    proPlan: 'Kitaalamu',
    enterprisePlan: 'Biashara',
    perMonth: '/mwezi',
    forever: 'milele',
    getStarted: 'Anza',
    mostPopular: 'Maarufu',
    email: 'Barua pepe',
    password: 'Nenosiri',
    fullName: 'Jina kamili',
    signIn: 'Ingia',
    signUp: 'Jisajili',
    footerText: 'Msaada wa maamuzi ya kliniki unaotegemea ushahidi',
    division: 'Kitengo cha 3',
    loading: 'Inapakia...',
    search: 'Tafuta',
    filter: 'Chuja',
    allSpecialties: 'Utaalamu Wote',
    allLevels: 'Viwango Vyote',
    showing: 'Kuonyesha',
    of: 'ya',
    results: 'matokeo',
    noResults: 'Hakuna matokeo',
    tryAdjusting: 'Jaribu kubadilisha utafutaji'
  }
};

// Language metadata
export const languageOptions = [
  { code: 'en' as Language, name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'fr' as Language, name: 'French', nativeName: 'Français', dir: 'ltr' },
  { code: 'pt' as Language, name: 'Portuguese', nativeName: 'Português', dir: 'ltr' },
  { code: 'ar' as Language, name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
  { code: 'sw' as Language, name: 'Swahili', nativeName: 'Kiswahili', dir: 'ltr' }
];

// Translation helper
export const t = (key: string, lang: Language = 'en'): string => {
  const keys = key.split('.');
  let value: string | Translation = translations[lang];
  
  for (const k of keys) {
    if (typeof value === 'object' && value !== null && k in value) {
      value = value[k];
    } else {
      // Fallback to English
      value = translations.en;
      for (const k2 of keys) {
        if (typeof value === 'object' && value !== null && k2 in value) {
          value = value[k2];
        }
      }
      break;
    }
  }
  
  return typeof value === 'string' ? value : key;
};
